/**
 * JWT claim reading — docs/spec/00 §2 (roles), 06 R1 (proactive refresh).
 *
 * This DECODES; it does not verify. Signature verification is the api's job and
 * the api is the authority on every permission (04 §7). What the client needs
 * from the token is (a) which nav and actions to show, and (b) when to refresh
 * before expiry. Treating a decoded claim as a security decision would be a
 * mistake — a user can edit their own token; they cannot edit the api's answer.
 */

/** Roles the client gates on (00 §2). */
export const ROLES = ['PHYSICAL_TRANSCRIBER', 'PHYSICAL_TRANSCRIBER_ADMIN'] as const
export type Role = (typeof ROLES)[number]

export interface TokenClaims {
  /** Seconds since epoch, or null when the token carries no `exp`. */
  readonly exp: number | null
  /** Recognised roles — these drive nav and action visibility. */
  readonly roles: readonly Role[]
  /**
   * Every role string the token carried, unfiltered.
   *
   * Real tokens include roles this client does not gate on — `READ_SCANS` ships
   * alongside the two above (observed 2026-07-29). Keeping the raw list means a
   * new role is visible when debugging rather than silently discarded, and
   * gating on one later costs nothing.
   */
  readonly rawRoles: readonly string[]
  readonly userId: string | null
  /**
   * The identifier the api's own records use.
   *
   * `userId` is a decimal string ("2"); a job's `assignee_id` is a UUID
   * ("de7ec7ed-0000-0000-0000-0000000000a1"). They are the SAME user — the
   * token carries the uuid too, unhyphenated. Comparing the wrong one made
   * every job a transcriber had just claimed look like someone else's, and the
   * workspace locked itself read-only (verified against staging 2026-07-29).
   */
  readonly userUuid: string | null
  readonly name: string | null
  /** `access` or `refresh`. Both token types share a payload shape. */
  readonly tokenType: string | null
}

function decodeSegment(segment: string): unknown {
  // base64url → base64, then pad. atob rejects unpadded input.
  const base64 = segment.replace(/-/g, '+').replace(/_/g, '/')
  const padded = base64.padEnd(base64.length + ((4 - (base64.length % 4)) % 4), '=')
  const binary = atob(padded)
  // `charCodeAt` rather than `codePointAt`: atob yields a binary string whose
  // units are all < 256, and charCodeAt always returns a number — so there is
  // no impossible-but-required `?? 0` fallback to leave permanently untested.
  const bytes = Uint8Array.from(binary, (c) => c.charCodeAt(0))
  return JSON.parse(new TextDecoder().decode(bytes))
}

function asRecord(value: unknown): Record<string, unknown> | null {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null
}

/**
 * Roles live at `identity.roles` — confirmed against the api 2026-07-29, NOT a
 * top-level claim. The fallbacks below are defensive only; if one of them ever
 * fires it means the token shape moved and something is wrong.
 */
function readRawRoles(payload: Record<string, unknown>): string[] {
  const identity = asRecord(payload['identity'])
  const candidates = [
    identity?.['roles'],
    identity?.['role'],
    payload['roles'],
    payload['role'],
    asRecord(payload['user_claims'])?.['roles'],
  ]

  for (const candidate of candidates) {
    const list =
      typeof candidate === 'string' ? [candidate] : Array.isArray(candidate) ? candidate : null
    if (list === null) continue
    const strings = list.filter((r): r is string => typeof r === 'string')
    if (strings.length > 0) return strings
  }

  // Least privilege: an unrecognised token shape grants nothing. The user sees
  // a transcriber-minus view rather than accidentally being handed admin nav.
  return []
}

function readString(payload: Record<string, unknown>, keys: readonly string[]): string | null {
  const identity = asRecord(payload['identity'])
  for (const key of keys) {
    const value = identity?.[key] ?? payload[key]
    if (typeof value === 'string' && value.trim() !== '') return value.trim()
  }
  return null
}

/**
 * A display name for the rail (04 §2).
 *
 * The token carries `first_name` / `last_name` separately — there is no `name`
 * claim — so compose them. Tolerates either half being missing rather than
 * rendering "Web undefined" at someone all day.
 */
function readDisplayName(payload: Record<string, unknown>): string | null {
  const composed = [readString(payload, ['first_name']), readString(payload, ['last_name'])]
    .filter((part): part is string => part !== null)
    .join(' ')

  return composed !== '' ? composed : readString(payload, ['name', 'full_name', 'username'])
}

/** Decode a JWT's claims. Returns null for anything unparseable. */
export function decodeToken(token: string): TokenClaims | null {
  // Destructured rather than length-checked so the payload segment arrives
  // already narrowed: `segments[1] ?? ''` would be a branch that can never be
  // taken, i.e. one that no test could ever cover honestly.
  const [, payloadSegment, signature, ...extra] = token.split('.')
  if (payloadSegment === undefined || signature === undefined || extra.length > 0) return null

  let payload: Record<string, unknown> | null
  try {
    payload = asRecord(decodeSegment(payloadSegment))
  } catch {
    return null
  }
  if (payload === null) return null

  const exp = payload['exp']
  const rawRoles = readRawRoles(payload)

  return {
    exp: typeof exp === 'number' && Number.isFinite(exp) ? exp : null,
    roles: rawRoles.filter((r): r is Role => (ROLES as readonly string[]).includes(r)),
    rawRoles,
    // `user_id` is a decimal string in the token (confirmed 2026-07-29); `did`
    // is the phone number and is the last resort, not an identifier we prefer.
    userId: readString(payload, ['user_id', 'id', 'sub', 'did']),
    userUuid: readString(payload, ['user_uuid', 'uuid']),
    name: readDisplayName(payload),
    tokenType: readString(payload, ['type']),
  }
}

export function hasRole(claims: TokenClaims | null, role: Role): boolean {
  return claims?.roles.includes(role) ?? false
}

/** Admin ⊇ transcriber (00 §2), so an admin passes a transcriber check. */
export function isAdmin(claims: TokenClaims | null): boolean {
  return hasRole(claims, 'PHYSICAL_TRANSCRIBER_ADMIN')
}

export function isTranscriber(claims: TokenClaims | null): boolean {
  return hasRole(claims, 'PHYSICAL_TRANSCRIBER') || isAdmin(claims)
}

/**
 * Should this token be refreshed now? — 06 R1's proactive refresh.
 *
 * `skewSeconds` is the head start: refreshing shortly BEFORE expiry means most
 * requests never 401 at all, so the user never even reaches the silent-retry
 * path. Token TTLs are still unknown (Q2 residual), which is precisely why this
 * keys off the token's own `exp` rather than a configured interval.
 *
 * A token with no `exp` is treated as NOT expiring — we cannot know better, and
 * refreshing on every request would be worse than trusting the 401 path.
 */
export function isExpiringSoon(
  claims: TokenClaims | null,
  nowMs: number,
  skewSeconds = 60,
): boolean {
  if (claims?.exp == null) return false
  return claims.exp - skewSeconds <= Math.floor(nowMs / 1000)
}
